<template>
  <div class="product-thumbnail-container">
    <div v-if="product" :class="alignmentClass">
      <div :class="['product-image', { clickAction: clickAction === 'fireEvent' }]">
        <div :id="`product-thumbnail-${product?.id}`" class="product-thumbnail-image-container">
          <a
            :tabindex="-1"
            :href="productUrl"
            :aria-labelledby="`product-thumbnail-${product?.id}`"
            :title="product?.detail?.name"
            target="_self"
            draggable="false"
            @click="handleImageClick"
          >
            <div class="v-product-thumbnail-image-container">
              <AtomicPicture
                :alt="product?.detail?.name"
                :image-url="primaryImageUrl"
                :image-intrinsic-width="imageWidth"
                :aspect-ratio="imageAspectRatio"
                :set-scene7-hei="true"
                retina-quality="55,0"
                :pt="{
                  img: {
                    fetchPriority: priority,
                    loading: priority === 'high' ? 'eager' : 'lazy',
                  },
                }"
                :native-lazy="false"
              />
            </div>
          </a>
        </div>
      </div>
      <ProductDescription
        :class="descriptionMarginClass"
        :product="product"
        :url="productUrl"
        :click-action="clickAction"
        :mcom-font-weight="mcomFontWeight"
        @get-more-details="handleImageClick"
      />
    </div>
  </div>
</template>

<script setup>
import { computed } from 'vue';
import AtomicPicture from '@atomic-ui/picture/Picture';
import ProductDescription from './ProductThumbnail/ProductDescription';
import { BCOM_URL_TEMPLATE, MCOM_URL_TEMPLATE } from '#shared/endpoints';
import { getProductThumbnailUrl } from '@/utils/TransformProduct';
import { SAFE_URL_PATTERN, IMAGE_ASPECT_RATIO } from '#shared/constants';
import useBrand from '@/composables/useBrand';

const { isBcom } = useBrand();

const props = defineProps({
  product: {
    type: Object,
    default: () => ({}),
  },
  alignment: {
    type: String,
    default: 'vertical',
    validator: (val) => ['vertical', 'horizontal'].includes(val),
  },
  imageWidth: {
    type: Number,
    default: undefined,
  },
  clickAction: {
    type: String,
    default: 'linkToPdp',
    validator: (val) => ['linkToPdp', 'disabled', 'fireEvent'].includes(val),
  },
  urlTemplate: {
    type: Object,
    required: false,
    default: undefined,
  },
  mcomFontWeight: {
    type: Boolean,
    default: false,
  },
  imageAspectRatio: {
    type: Number,
    default: IMAGE_ASPECT_RATIO,
  },
  priority: {
    type: String,
    default: 'auto',
  },
});

const emit = defineEmits(['getProductDetails']);

const brandUrlTemplate = computed(() => {
  if (props.urlTemplate) return props.urlTemplate;
  return {
    product: isBcom ? BCOM_URL_TEMPLATE : MCOM_URL_TEMPLATE,
  };
});

const primaryImageUrl = computed(() => {
  const imagery = props.product?.imagery;
  if (!imagery?.primaryImage?.filePath) return undefined;

  const urlTemplate = imagery.urlTemplate || brandUrlTemplate.value.product;
  return getProductThumbnailUrl(urlTemplate, imagery.primaryImage.filePath, props.imageWidth);
});

const alignmentClass = computed(() => ({
  'product-thumbnail-container': true,
  'horizontal-alignment': props.alignment === 'horizontal',
  'vertical-alignment': props.alignment === 'vertical',
}));

const cssImageWidth = computed(() => {
  return props.alignment === 'vertical' ? '100%' : `${props.imageWidth}px`;
});

const descriptionMarginClass = computed(() => {
  return !props.product?.detail?.bottomOverlay && props.alignment === 'vertical' ? 'margin-top-xxs' : '';
});

const productUrl = computed(() => {
  const url = props.product?.identifier?.productUrl;
  return url && SAFE_URL_PATTERN.test(url) ? url : undefined;
});

const handleImageClick = (event) => {
  if (props.clickAction === 'fireEvent') {
    emit('getProductDetails', props.product);
    event.preventDefault();
    return false;
  }
  if (props.clickAction === 'disabled') {
    event.preventDefault();
    return false;
  }
  return true;
};
</script>

<style lang="scss" scoped>
.product-thumbnail-container {
  .product-image {
    position: relative;
    width: v-bind(cssImageWidth);
  }

  .clickAction {
    cursor: pointer;
  }
}

.horizontal-alignment {
  display: flex;

  .product-description {
    order: 1;
    margin-left: rem-calc(25);

    .product-name {
      margin-bottom: rem-calc(15);
    }
  }
}
</style>

<style lang="scss" scoped brand="mcom">
.product-thumbnail-image-container {
  margin-bottom: rem-calc(12);

  @include breakpoint(medium down) {
    margin-bottom: rem-calc(8);
  }
}

.v-product-thumbnail-image-container {
  position: relative;
  container-type: inline-size;
}
</style>
